<template>
  <!-- The edition's sheet, on the design system's dialog like the blocking notification: the edition's
       date and version over the title (14, the second ink), its entries as the body, which scrolls when
       they do not fit, and «Entendido» fixed under them. «Nuevo» is the brand's tag; the other kinds are
       the neutral one (a green or an orange here would read as a verdict, norm 11). A tap on the scrim
       does not close it: the X, «Entendido», back and Escape do. -->
  <BzDialog
    card
    :visible="visible"
    band="blocking"
    :eyebrow="subtitle || undefined"
    :title="$t('message.changelogTitle')"
    :close-label="$t('message.close')"
    title-testid="changelog-title"
    close-testid="changelog-close"
    @dismiss="dismiss"
  >
    <div class="changelog" data-testid="changelog">
      <article v-for="item in featuredItems" :key="item.id" class="changelog__item" data-testid="changelog-item">
        <span class="bz-chip" :class="{ 'bz-chip--brand': item.type === 'new' }">
          <BzIcon v-if="item.type === 'new'" name="campaign" />{{ categoryLabel(item.type) }}
        </span>
        <h3 class="changelog__title">{{ item.title }}</h3>
        <p v-if="item.body" class="changelog__text">{{ item.body }}</p>
        <ul v-if="visiblePoints(item).length" class="changelog__points">
          <li v-for="(point, index) in visiblePoints(item)" :key="index">{{ point }}</li>
        </ul>
        <img
          v-if="item.image && !brokenImages[item.id]"
          class="changelog__shot"
          :src="item.image.url"
          :alt="item.image.alt"
          loading="lazy"
          @error="brokenImages[item.id] = true"
        />
      </article>

      <!-- Every fix of the edition collapses into one row: a week of bug fixing must
           never out-weigh a feature on a screen read at arm's length. -->
      <article v-if="fixedSummary" class="changelog__item">
        <span class="bz-chip">{{ categoryLabel('fixed') }}</span>
        <p class="changelog__text">{{ fixedSummary }}</p>
      </article>
    </div>

    <template #footer>
      <div class="changelog__actions">
        <BzButton kind="primary" size="lg" block :label="$t('message.changelogAck')" data-testid="changelog-ack" @click="dismiss" />
        <a
          class="changelog__archive"
          data-testid="changelog-see-all"
          :href="edition ? edition.url : undefined"
          target="_blank"
          rel="noopener noreferrer"
          @click="markAsSeen"
        >{{ $t('message.changelogSeeAll') }}</a>
      </div>
    </template>
  </BzDialog>
</template>

<script>
import { useI18n } from 'vue-i18n';
import Logger from '@/logger';
import BzButton from '@/ui/components/BzButton.vue';
import BzDialog from '@/ui/components/BzDialog.vue';
import BzIcon from '@/ui/components/BzIcon.vue';

const logger = new Logger({ screen: 'ChangelogDialog' });

// The changelog is published on its own cadence, independently of the app: an edition
// reaches the tablet by appearing in this feed, with no deploy involved. That is why the
// modal is gated on the edition id and not on the app version — the version key is what
// kept this component dormant for months.
const FEED_BASE = process.env.VUE_APP_CHANGELOG_FEED_URL || 'https://bronze.vision/novedades/feed/v1/tracker';
const FEED_LANGUAGES = ['es', 'fr', 'en'];
const FEED_FALLBACK_LANGUAGE = 'es';
const FEED_TIMEOUT_MS = 8000;

// Enough history that no edition can re-open, small enough that the record can't grow
// forever on a tablet that never gets wiped.
const SEEN_HISTORY = 20;

// A restaurant's wifi is routinely down exactly when the tablet boots, and the app can
// stay open for days — one failed fetch must not cost the whole shift. Retried on the
// next screen change, a few times, then dropped.
const MAX_FETCH_ATTEMPTS = 3;

// The feed already caps an item at three bullets, and editions use all three to carry
// the concrete numbers ("15 minutes instead of 8"). Dropping one on a tablet loses the
// part that is actually actionable, so every point the edition wrote is rendered.
const MAX_POINTS = 3;

export default {
  name: 'ChangelogDialog',
  components: { BzButton, BzDialog, BzIcon },
  props: {
    db: {
      type: Object,
      required: false,
      default: null
    },
    // The App only turns this on when the camera screen is idle and no order is
    // waiting for a photo. A modal on top of live kitchen work costs an order.
    active: {
      type: Boolean,
      default: false
    },
    // Bumped by Ajustes → "Novedades": an explicit request to re-read the latest
    // edition, so neither the seen-gate nor the idle-gate applies to it.
    openSignal: {
      type: Number,
      default: 0
    }
  },
  setup() {
    const { locale } = useI18n();
    return { i18nLocale: locale };
  },
  data() {
    return {
      edition: null,
      visible: false,
      // Set once the feed answers: a dismissed edition must not come back on the next
      // screen change. A failed fetch leaves it false so the check can be retried.
      checked: false,
      checking: false,
      attempts: 0,
      // An unseen edition was fetched but the kitchen got busy during the fetch:
      // hold it and open on the next idle activation instead of over live work.
      pendingOpen: false,
      brokenImages: {}
    };
  },
  computed: {
    featuredItems() {
      if (!this.edition) return [];
      return this.edition.items.filter((item) => item.type !== 'fixed');
    },
    fixedSummary() {
      if (!this.edition) return '';
      return this.edition.items
        .filter((item) => item.type === 'fixed')
        .map((item) => item.title)
        .join(' · ');
    },
    subtitle() {
      if (!this.edition) return '';
      const version = this.edition.versions && this.edition.versions.tracker;
      const parts = [this.formattedDate];
      if (version) parts.push(`Bronze Tracker ${version}`);
      return parts.filter(Boolean).join(' · ');
    },
    formattedDate() {
      if (!this.edition) return '';
      // Parsed field by field on purpose: `new Date('2026-08-24')` is UTC midnight and
      // renders as the previous day west of Greenwich.
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(this.edition.date || '');
      if (!match) return '';
      const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      try {
        return date.toLocaleDateString(this.feedLanguage, { day: 'numeric', month: 'long' });
      } catch (_) {
        return this.edition.date;
      }
    },
    feedLanguage() {
      return FEED_LANGUAGES.includes(this.i18nLocale) ? this.i18nLocale : FEED_FALLBACK_LANGUAGE;
    }
  },
  watch: {
    db: {
      immediate: true,
      handler() {
        this.maybeShow();
      }
    },
    active: {
      immediate: true,
      handler() {
        this.maybeShow();
      }
    },
    openSignal() {
      this.openManually();
    }
  },
  methods: {
    /**
     * Opens the modal at most once, and only when the app is in a state where an
     * interruption is harmless. Everything here fails silently: restaurants lose
     * connectivity routinely and a changelog is never worth an error state.
     */
    async maybeShow() {
      if (!this.db || !this.active) return;

      // An unseen edition fetched earlier was held back because the kitchen got busy:
      // this activation is the idle moment it was waiting for.
      if (this.pendingOpen) {
        this.pendingOpen = false;
        this.visible = true;
        return;
      }

      if (this.checked || this.checking) return;
      if (this.attempts >= MAX_FETCH_ATTEMPTS) return;

      this.checking = true;
      this.attempts += 1;
      try {
        const edition = await this.fetchLatestEdition();
        // The feed answered: whatever it said is final for this session.
        this.checked = true;
        if (!edition) return;

        const seen = await this.readSeenEditions();
        // `null` means the read itself failed — better to skip an edition than to
        // re-open the same modal on every start-up.
        if (!seen || seen.includes(edition.id)) return;

        this.edition = edition;
        // The fetch took time; if an order arrived meanwhile the gate is closed again —
        // keep the edition and let the next idle activation open it.
        if (!this.active) {
          this.pendingOpen = true;
          return;
        }
        this.visible = true;
      } catch (error) {
        // Network failure only. Nothing is surfaced to the kitchen; the next screen
        // change retries until MAX_FETCH_ATTEMPTS.
        logger.console('Changelog feed unavailable', { error });
      } finally {
        this.checking = false;
      }
    },

    /**
     * Ajustes → "Novedades". An explicit request: ignores the seen-gate, the idle-gate
     * and the retry budget — but still fails silently, and dismissing still counts as
     * having read the edition.
     */
    async openManually() {
      if (this.visible) return;
      if (!this.edition) {
        if (this.checking) return;
        this.checking = true;
        try {
          this.edition = await this.fetchLatestEdition();
          this.checked = true;
        } catch (error) {
          logger.console('Changelog feed unavailable', { error });
        } finally {
          this.checking = false;
        }
      }
      if (this.edition) this.visible = true;
    },

    /** Latest edition, `null` when there is nothing to show. Throws if the feed is unreachable. */
    async fetchLatestEdition() {
      // Demo mode is offline by construction, and this is the single door to the network:
      // both the idle auto-open and Ajustes → "Novedades" come through here. Without this
      // guard the second one reaches bronze.vision and paints a real, dated, published
      // edition into a session whose whole purpose is to contain nothing real — including
      // the screenshots the changelog engine publishes from it. Instead of hiding the
      // feature, the demo serves a fixture (already-published text, no images, no fetch),
      // so the modal stays photographable and the Ajustes row keeps working.
      if (__BRONZE_DEMO__) {
        const { demoEdition } = await import('@/demo/edition');
        return demoEdition(this.feedLanguage);
      }

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FEED_TIMEOUT_MS);
      try {
        const response = await fetch(`${FEED_BASE}/${this.feedLanguage}.json`, {
          signal: controller.signal,
          cache: 'no-store'
        });
        if (!response.ok) throw new Error(`Changelog feed responded ${response.status}`);

        const feed = await response.json();
        const editions = feed && Array.isArray(feed.editions) ? feed.editions : [];
        // The feed is newest-first and never carries an edition without items, but a
        // modal is not the place to trust a contract blindly.
        const latest = editions[0];
        if (!latest || !latest.id || !Array.isArray(latest.items) || latest.items.length === 0) {
          return null;
        }
        return latest;
      } finally {
        clearTimeout(timer);
      }
    },

    /** Resolves the seen ids, or `null` when IndexedDB can't be read. */
    readSeenEditions() {
      return new Promise((resolve) => {
        try {
          const request = this.db.transaction('settings', 'readonly')
            .objectStore('settings')
            .get('changelog');
          request.onsuccess = (event) => {
            const stored = event.target.result;
            resolve(stored && Array.isArray(stored.seenEditions) ? stored.seenEditions : []);
          };
          request.onerror = () => resolve(null);
        } catch (_) {
          resolve(null);
        }
      });
    },

    markAsSeen() {
      if (!this.db || !this.edition) return;
      const editionId = this.edition.id;
      try {
        const store = this.db.transaction('settings', 'readwrite').objectStore('settings');
        const request = store.get('changelog');
        request.onsuccess = (event) => {
          // The record is spread back in so the legacy `seenVersions` key survives: a
          // tablet upgrading from the version-keyed changelog sees the current edition
          // once, which is the correct behaviour.
          const stored = event.target.result || {};
          const seen = Array.isArray(stored.seenEditions) ? stored.seenEditions : [];
          if (!seen.includes(editionId)) seen.push(editionId);
          store.put({ ...stored, key: 'changelog', seenEditions: seen.slice(-SEEN_HISTORY) });
        };
        request.onerror = (event) => {
          logger.warning('Could not persist the seen changelog edition', { error: event.target.error });
        };
      } catch (error) {
        logger.warning('Could not persist the seen changelog edition', { error });
      }
    },

    dismiss() {
      this.visible = false;
      // A dismissed edition is read: never re-open it on a later idle moment.
      this.pendingOpen = false;
      this.markAsSeen();
    },

    visiblePoints(item) {
      const points = Array.isArray(item.points) ? item.points : [];
      return points.slice(0, MAX_POINTS);
    },

    categoryLabel(type) {
      const keys = {
        new: 'message.changelogTagNew',
        improved: 'message.changelogTagImproved',
        integration: 'message.changelogTagIntegration',
        fixed: 'message.changelogTagFixed'
      };
      return this.$t(keys[type] || keys.improved);
    }
  }
};
</script>

<style scoped>
/* The entries one under the other, a hairline between them: they are read in order, as a list, not as
   cards inside a card. */
.changelog {
  display: flex;
  flex-direction: column;
}

.changelog__item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--bz-space-2);
  padding: var(--bz-space-4) 0;
  border-top: 1px solid var(--bz-edge);
}

.changelog__item:first-child {
  padding-top: 0;
  border-top: 0;
}

.changelog__title {
  margin: 0;
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  font-weight: var(--bz-weight-label);
  color: var(--bz-ink);
}

.changelog__text,
.changelog__points {
  margin: 0;
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-body);
  color: var(--bz-ink-2);
}

.changelog__points {
  padding-inline-start: var(--bz-space-5);
}

.changelog__points li + li {
  margin-top: var(--bz-space-1);
}

/* Screenshots are read from the top: cropping the bottom keeps the header visible. */
.changelog__shot {
  display: block;
  width: 100%;
  max-height: 190px;
  margin-top: var(--bz-space-2);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-md);
  object-fit: cover;
  object-position: top;
}

.changelog__actions {
  display: flex;
  flex-direction: column;
  gap: var(--bz-target-gap);
  width: 100%;
}

/* A link, drawn as the system's quiet button: 56 high, centred, in the action ink, no underline. */
.changelog__archive {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: var(--bz-target-md);
  box-sizing: border-box;
  padding: 0 var(--bz-space-4);
  border-radius: var(--bz-radius-md);
  color: var(--bz-action-ink);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-label);
  text-decoration: none;
}

.changelog__archive:active {
  background: var(--bz-control);
}
</style>
